import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FiBell,
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiEye,
  FiEyeOff,
  FiEdit2,
  FiFileText,
  FiRefreshCw,
  FiSave,
  FiSend,
  FiShield,
  FiTrash2,
  FiTool,
  FiUpload,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";

import { ClientePageShell } from "../../../components/ClientePageShell";
import {
  alterarStatusCartaMensal,
  atualizarCartaMensal,
  atualizarAvisoPlataforma,
  atualizarDisponibilidadeInformeImpostoRenda,
  ComunicacaoCliente,
  ComunicacaoMutationResult,
  excluirCartaMensal,
  obterGestaoComunicacoes,
  publicarCartaMensal,
  substituirArquivoCartaMensal,
} from "../../../services/comunicacoesCliente";
import { executarRequisicaoAutenticada } from "../../../services/authenticatedRequest";
import { obterTodosClientes } from "../../../services/gestor";
import { baixarExtratoPdfGestor } from "../../../services/statementReport";
import { baixarInformeImpostoRendaGestor } from "../../../services/taxReport";
import "./PainelControle.css";

const defaultNotices: ComunicacaoCliente[] = [
  {
    id: -1,
    tipo: "CotaManutencao",
    titulo: "Cota em manutenção",
    mensagem: "A atualização da cota está temporariamente em manutenção.",
    ativo: false,
    criadoEm: "",
    atualizadoEm: "",
    lida: false,
  },
  {
    id: -2,
    tipo: "SistemaAtualizacao",
    titulo: "Sistema em atualização pela equipe Omex",
    mensagem:
      "A equipe Omex está realizando uma atualização programada na plataforma.",
    ativo: false,
    criadoEm: "",
    atualizadoEm: "",
    lida: false,
  },
];

const currentMonth = new Date().toISOString().slice(0, 7);
const previousCalendarYear = new Date().getFullYear() - 1;

const formatDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const defaultStatementStartDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);

  return formatDateInputValue(date);
};

const statementServiceOptions = [
  { id: 1, label: "Assessoria" },
  { id: 3, label: "Private" },
];

const noticeLabels: Record<
  ComunicacaoCliente["tipo"],
  { scope: string; title: string; icon: React.ReactNode }
> = {
  CartaMensal: {
    scope: "Relatório",
    title: "Carta Mensal",
    icon: <FiFileText />,
  },
  CotaManutencao: {
    scope: "Operacional",
    title: "Cota em manutenção",
    icon: <FiTool />,
  },
  SistemaAtualizacao: {
    scope: "Plataforma",
    title: "Sistema em atualização",
    icon: <FiRefreshCw />,
  },
  InformeImpostoRenda: {
    scope: "Fiscal",
    title: "Informe de Imposto de Renda",
    icon: <FiFileText />,
  },
};

const getCommunicationReferenceYear = (comunicacao?: ComunicacaoCliente) => {
  const year = Number(comunicacao?.mesReferencia?.slice(0, 4));
  return Number.isFinite(year) ? year : undefined;
};

const buildTaxReportNotice = (
  anoCalendario: number,
  comunicacao?: ComunicacaoCliente
): ComunicacaoCliente => ({
  id: comunicacao?.id ?? -4,
  tipo: "InformeImpostoRenda",
  titulo:
    comunicacao?.titulo ??
    `Informe de Imposto de Renda ${anoCalendario} disponível`,
  mensagem:
    comunicacao?.mensagem ??
    `Seu Informe de Imposto de Renda referente ao ano-calendário ${anoCalendario} já está disponível para geração na plataforma. Acesse o menu Extrato para baixar o documento.`,
  mesReferencia:
    comunicacao?.mesReferencia ?? `${anoCalendario}-12-01T00:00:00Z`,
  ativo: comunicacao?.ativo ?? false,
  criadoEm: comunicacao?.criadoEm ?? "",
  atualizadoEm: comunicacao?.atualizadoEm ?? "",
  lida: comunicacao?.lida ?? false,
});

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const formatReferenceMonth = (date?: string) => {
  if (!date) return "Sem referência";

  const match = date.match(/^(\d{4})-(\d{2})/);
  if (!match) return date;

  return new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)));
};

const formatDateTime = (date?: string) => {
  if (!date) return "Sem atualização";

  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return "Sem atualização";

  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(value);
};

const formatBytes = (bytes?: number) => {
  if (!bytes) return "PDF";
  const megaBytes = bytes / (1024 * 1024);
  return `${megaBytes.toLocaleString("pt-BR", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  })} MB`;
};

const createFeedback = (
  action: string,
  result?: ComunicacaoMutationResult
) => {
  const sent = result?.emailsEnviados ?? 0;
  if (result?.notificacaoFalhou) {
    return `${action} As notificações por e-mail não foram enviadas; o conteúdo ficou disponível na plataforma.`;
  }

  return sent > 0 ? `${action} ${sent} e-mails enviados.` : action;
};

const parseApiData = (data: any) => {
  if (typeof data !== "string") {
    return data;
  }

  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
};

const getErrorMessage = (error: any, fallback: string) => {
  const data = parseApiData(error?.response?.data);
  const message =
    data?.mensagem ||
    data?.message ||
    data?.detail ||
    data?.title ||
    error?.message;

  return typeof message === "string" && message.trim()
    ? message
    : fallback;
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

type CartaMensalEditDraft = {
  titulo: string;
  descricao: string;
  mesReferencia: string;
};

type ClienteInformeImpostoRenda = {
  nome?: string;
  idCredenciais: string;
  email?: string;
};

const PainelControle = () => {
  const [avisos, setAvisos] = useState<ComunicacaoCliente[]>(defaultNotices);
  const [cartas, setCartas] = useState<ComunicacaoCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingNotice, setSavingNotice] = useState<string>();
  const [savingTaxReport, setSavingTaxReport] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [taxReportYear, setTaxReportYear] = useState(previousCalendarYear);
  const [taxReportNotice, setTaxReportNotice] = useState<ComunicacaoCliente>(
    buildTaxReportNotice(previousCalendarYear)
  );
  const [clientesInforme, setClientesInforme] = useState<
    ClienteInformeImpostoRenda[]
  >([]);
  const [loadingClientesInforme, setLoadingClientesInforme] = useState(false);
  const [clienteInformeSelecionado, setClienteInformeSelecionado] = useState("");
  const [anoInformeManual, setAnoInformeManual] = useState(previousCalendarYear);
  const [gerandoInformeManual, setGerandoInformeManual] = useState(false);
  const [clienteExtratoSelecionado, setClienteExtratoSelecionado] = useState("");
  const [servicoExtrato, setServicoExtrato] = useState(1);
  const [dataInicialExtrato, setDataInicialExtrato] = useState(
    defaultStatementStartDate
  );
  const [dataFinalExtrato, setDataFinalExtrato] = useState(() =>
    formatDateInputValue(new Date())
  );
  const [gerandoExtratoManual, setGerandoExtratoManual] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [mesReferencia, setMesReferencia] = useState(currentMonth);
  const [arquivo, setArquivo] = useState<File>();
  const [publicar, setPublicar] = useState(true);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [editingCartaId, setEditingCartaId] = useState<number>();
  const [editDraft, setEditDraft] = useState<CartaMensalEditDraft>({
    titulo: "",
    descricao: "",
    mesReferencia: currentMonth,
  });
  const [savingCartaId, setSavingCartaId] = useState<number>();

  const loadManagement = useCallback(async () => {
    setLoading(true);

    try {
      const result = await obterGestaoComunicacoes();
      const noticesByType = new Map(
        (result.avisos || []).map((item) => [item.tipo, item])
      );
      setAvisos(
        defaultNotices.map((notice) => noticesByType.get(notice.tipo) || notice)
      );
      const taxReportCommunication = (result.avisos || []).find(
        (item) =>
          item.tipo === "InformeImpostoRenda" &&
          getCommunicationReferenceYear(item) === taxReportYear
      );
      setTaxReportNotice(
        buildTaxReportNotice(taxReportYear, taxReportCommunication)
      );
      setCartas(result.cartasMensais || []);
      setError("");
    } catch (error) {
      setError(
        getErrorMessage(
          error,
          "Não foi possível carregar a gestão de mensageria."
        )
      );
    } finally {
      setLoading(false);
    }
  }, [taxReportYear]);

  useEffect(() => {
    loadManagement();
  }, [loadManagement]);

  const loadClientesInforme = useCallback(async () => {
    setLoadingClientesInforme(true);

    try {
      const response = await executarRequisicaoAutenticada((token) =>
        obterTodosClientes(token)
      );
      const data = parseApiData(response?.data);
      const clientes: ClienteInformeImpostoRenda[] =
        data?.resultado?.clientes ||
        data?.camposAdicionais?.clientes ||
        data?.clientes ||
        [];

      const clientesValidos = clientes
        .filter((cliente) => Boolean(cliente.idCredenciais))
        .sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));

      setClientesInforme(clientesValidos);
      setClienteInformeSelecionado(
        (current) => current || clientesValidos[0]?.idCredenciais || ""
      );
      setClienteExtratoSelecionado(
        (current) => current || clientesValidos[0]?.idCredenciais || ""
      );
    } catch (error) {
      setError(
        getErrorMessage(error, "Não foi possível carregar a lista de clientes.")
      );
    } finally {
      setLoadingClientesInforme(false);
    }
  }, []);

  useEffect(() => {
    loadClientesInforme();
  }, [loadClientesInforme]);

  const activeNotices = useMemo(
    () => avisos.filter((aviso) => aviso.ativo).length,
    [avisos]
  );
  const publishedReports = useMemo(
    () => cartas.filter((carta) => carta.ativo).length,
    [cartas]
  );
  const latestUpdate = useMemo(() => {
    const dates = [...avisos, ...cartas, taxReportNotice]
      .map((item) => item.atualizadoEm || item.criadoEm)
      .filter(Boolean)
      .map((date) => new Date(date).getTime())
      .filter((date) => !Number.isNaN(date));

    if (!dates.length) return "Sem atualização";
    return formatDateTime(new Date(Math.max(...dates)).toISOString());
  }, [avisos, cartas, taxReportNotice]);

  const clienteInformeAtual = useMemo(
    () =>
      clientesInforme.find(
        (cliente) => cliente.idCredenciais === clienteInformeSelecionado
      ),
    [clienteInformeSelecionado, clientesInforme]
  );

  const clienteExtratoAtual = useMemo(
    () =>
      clientesInforme.find(
        (cliente) => cliente.idCredenciais === clienteExtratoSelecionado
      ),
    [clienteExtratoSelecionado, clientesInforme]
  );

  const updateNoticeField = (
    tipo: ComunicacaoCliente["tipo"],
    field: "titulo" | "mensagem",
    value: string
  ) => {
    setAvisos((current) =>
      current.map((item) =>
        item.tipo === tipo ? { ...item, [field]: value } : item
      )
    );
  };

  const saveNotice = async (aviso: ComunicacaoCliente) => {
    setSavingNotice(aviso.tipo);
    setError("");
    setSuccess("");

    try {
      const result = await atualizarAvisoPlataforma(aviso);
      setSuccess(createFeedback(`${aviso.titulo} atualizado.`, result));
      await loadManagement();
    } catch (error) {
      setError(getErrorMessage(error, "Não foi possível atualizar o aviso."));
      await loadManagement();
    } finally {
      setSavingNotice(undefined);
    }
  };

  const toggleNotice = async (aviso: ComunicacaoCliente) => {
    const updatedNotice = { ...aviso, ativo: !aviso.ativo };
    setAvisos((current) =>
      current.map((item) => (item.tipo === aviso.tipo ? updatedNotice : item))
    );
    await saveNotice(updatedNotice);
  };

  const updateTaxReportNoticeField = (
    field: "titulo" | "mensagem",
    value: string
  ) => {
    setTaxReportNotice((current) => ({ ...current, [field]: value }));
  };

  const handleTaxReportYearChange = (value: string) => {
    if (!value) {
      return;
    }

    const parsedYear = Number(value);

    if (!Number.isFinite(parsedYear)) {
      return;
    }

    setTaxReportYear(parsedYear);
    setTaxReportNotice(buildTaxReportNotice(parsedYear));
  };

  const saveTaxReportAvailability = async (ativo = taxReportNotice.ativo) => {
    const updatedNotice = { ...taxReportNotice, ativo };
    setTaxReportNotice(updatedNotice);
    setSavingTaxReport(true);
    setError("");
    setSuccess("");

    try {
      const result = await atualizarDisponibilidadeInformeImpostoRenda({
        anoCalendario: taxReportYear,
        titulo: updatedNotice.titulo,
        mensagem: updatedNotice.mensagem,
        ativo,
      });
      setSuccess(
        createFeedback(
          ativo
            ? "Informe de Imposto de Renda liberado."
            : "Informe de Imposto de Renda bloqueado.",
          result
        )
      );
      await loadManagement();
    } catch (error) {
      setError(
        getErrorMessage(
          error,
          "Não foi possível atualizar a disponibilidade do informe."
        )
      );
      await loadManagement();
    } finally {
      setSavingTaxReport(false);
    }
  };

  const gerarInformeManual = async (event: FormEvent) => {
    event.preventDefault();

    if (!clienteInformeSelecionado) {
      setError("Selecione um cliente para gerar o informe.");
      return;
    }

    setGerandoInformeManual(true);
    setError("");
    setSuccess("");

    try {
      const { blob, fileName } = await baixarInformeImpostoRendaGestor({
        idCredenciaisCliente: clienteInformeSelecionado,
        anoCalendario: anoInformeManual,
      });
      downloadBlob(blob, fileName);
      setSuccess(
        `Informe de Imposto de Renda gerado para ${clienteInformeAtual?.nome || "o cliente selecionado"
        }.`
      );
    } catch (error) {
      setError(
        getErrorMessage(error, "Não foi possível gerar o informe do cliente.")
      );
    } finally {
      setGerandoInformeManual(false);
    }
  };

  const gerarExtratoManual = async (event: FormEvent) => {
    event.preventDefault();

    if (!clienteExtratoSelecionado) {
      setError("Selecione um cliente para gerar o extrato.");
      return;
    }

    const dataInicial = new Date(`${dataInicialExtrato}T00:00:00`);
    const dataFinal = new Date(`${dataFinalExtrato}T23:59:59`);

    if (
      Number.isNaN(dataInicial.getTime()) ||
      Number.isNaN(dataFinal.getTime())
    ) {
      setError("Informe um período válido para gerar o extrato.");
      return;
    }

    if (dataInicial > dataFinal) {
      setError("A data inicial não pode ser maior que a data final.");
      return;
    }

    setGerandoExtratoManual(true);
    setError("");
    setSuccess("");

    try {
      const { blob, fileName } = await baixarExtratoPdfGestor({
        idCredenciaisCliente: clienteExtratoSelecionado,
        idServicoFinanceiro: servicoExtrato,
        dataInicial,
        dataFinal,
      });
      downloadBlob(blob, fileName);
      setSuccess(
        `Extrato gerado para ${clienteExtratoAtual?.nome || "o cliente selecionado"
        }.`
      );
    } catch (error) {
      setError(
        getErrorMessage(error, "Não foi possível gerar o extrato do cliente.")
      );
    } finally {
      setGerandoExtratoManual(false);
    }
  };

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    setArquivo(event.target.files?.[0]);
  };

  const submitReport = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!arquivo || arquivo.type !== "application/pdf") {
      setError("Selecione um arquivo PDF válido.");
      return;
    }

    if (arquivo.size > 15 * 1024 * 1024) {
      setError("O PDF deve ter no máximo 15 MB.");
      return;
    }

    setSavingReport(true);

    try {
      const arquivoBase64 = await readFileAsDataUrl(arquivo);
      const result = await publicarCartaMensal({
        titulo,
        descricao,
        mesReferencia: `${mesReferencia}-01`,
        nomeArquivo: arquivo.name,
        arquivoBase64,
        publicar,
      });
      setTitulo("");
      setDescricao("");
      setArquivo(undefined);
      setPublicar(true);
      setFileInputKey((current) => current + 1);
      setSuccess(createFeedback("Carta mensal publicada.", result));
      await loadManagement();
    } catch (error) {
      setError(
        getErrorMessage(error, "Não foi possível publicar a carta mensal.")
      );
    } finally {
      setSavingReport(false);
    }
  };

  const toggleReport = async (carta: ComunicacaoCliente) => {
    const ativo = !carta.ativo;
    setCartas((current) =>
      current.map((item) => (item.id === carta.id ? { ...item, ativo } : item))
    );
    setSavingCartaId(carta.id);
    setError("");
    setSuccess("");

    try {
      const result = await alterarStatusCartaMensal(carta.id, ativo);
      setSuccess(
        createFeedback(
          ativo ? "Carta mensal publicada." : "Carta mensal ocultada.",
          result
        )
      );
      await loadManagement();
    } catch (error) {
      setError(
        getErrorMessage(
          error,
          "Não foi possível alterar a visibilidade da carta."
        )
      );
      await loadManagement();
    } finally {
      setSavingCartaId(undefined);
    }
  };

  const startEditingCarta = (carta: ComunicacaoCliente) => {
    setEditingCartaId(carta.id);
    setEditDraft({
      titulo: carta.titulo,
      descricao: carta.mensagem,
      mesReferencia: carta.mesReferencia?.slice(0, 7) || currentMonth,
    });
    setError("");
    setSuccess("");
  };

  const cancelEditingCarta = () => {
    setEditingCartaId(undefined);
    setEditDraft({
      titulo: "",
      descricao: "",
      mesReferencia: currentMonth,
    });
  };

  const updateEditDraft = (
    field: keyof CartaMensalEditDraft,
    value: string
  ) => {
    setEditDraft((current) => ({ ...current, [field]: value }));
  };

  const saveCartaDetails = async (event: FormEvent) => {
    event.preventDefault();

    if (!editingCartaId) return;

    setSavingCartaId(editingCartaId);
    setError("");
    setSuccess("");

    try {
      const result = await atualizarCartaMensal(editingCartaId, {
        titulo: editDraft.titulo,
        descricao: editDraft.descricao,
        mesReferencia: `${editDraft.mesReferencia}-01`,
      });
      setSuccess(createFeedback("Carta mensal atualizada.", result));
      cancelEditingCarta();
      await loadManagement();
    } catch (error) {
      setError(
        getErrorMessage(error, "Não foi possível atualizar a carta mensal.")
      );
    } finally {
      setSavingCartaId(undefined);
    }
  };

  const replaceCartaFile = async (
    carta: ComunicacaoCliente,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    setError("");
    setSuccess("");

    if (file.type !== "application/pdf") {
      setError("Selecione um arquivo PDF válido.");
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      setError("O PDF deve ter no máximo 15 MB.");
      return;
    }

    setSavingCartaId(carta.id);

    try {
      const arquivoBase64 = await readFileAsDataUrl(file);
      const result = await substituirArquivoCartaMensal(carta.id, {
        nomeArquivo: file.name,
        arquivoBase64,
      });
      setSuccess(createFeedback("PDF da carta mensal substituído.", result));
      await loadManagement();
    } catch (error) {
      setError(
        getErrorMessage(error, "Não foi possível substituir o PDF da carta.")
      );
    } finally {
      setSavingCartaId(undefined);
    }
  };

  const deleteCarta = async (carta: ComunicacaoCliente) => {
    if (!window.confirm("Remover esta carta mensal e o PDF do acervo?")) {
      return;
    }

    setSavingCartaId(carta.id);
    setError("");
    setSuccess("");

    try {
      await excluirCartaMensal(carta.id);
      if (editingCartaId === carta.id) {
        cancelEditingCarta();
      }
      setSuccess("Carta mensal removida.");
      await loadManagement();
    } catch (error) {
      setError(getErrorMessage(error, "Não foi possível remover a carta."));
    } finally {
      setSavingCartaId(undefined);
    }
  };

  return (
    <ClientePageShell
      className="template-email-page"
      eyebrow="Painel de comando"
      title="Centro de controle"
      subtitle="Gerencie comunicações, relatórios fiscais, extratos de clientes e publicações operacionais em um único painel."
      actions={
        <button
          className="cliente-page-button secondary"
          type="button"
          onClick={loadManagement}
          disabled={loading}
        >
          <FiRefreshCw />
          {loading ? "Atualizando..." : "Atualizar"}
        </button>
      }
    >
      <section className="template-email-metrics" aria-label="Resumo do painel de comando">
        <article className="template-email-metric dark">
          <FiShield />
          <span>Canal</span>
          <strong>SendGrid</strong>
        </article>
        <article className="template-email-metric">
          <FiBell />
          <span>Avisos ativos</span>
          <strong>{activeNotices}</strong>
        </article>
        <article className="template-email-metric">
          <FiFileText />
          <span>Cartas publicadas</span>
          <strong>{publishedReports}</strong>
        </article>
        <article className="template-email-metric">
          <FiClock />
          <span>Última atualização</span>
          <strong>{latestUpdate}</strong>
        </article>
      </section>

      {(error || success) && (
        <div className={`template-email-feedback ${error ? "error" : "success"}`}>
          {error || success}
        </div>
      )}

      <section className="template-email-grid">
        <div className="template-email-main-flow">
          <section className="cliente-page-panel template-email-panel">
            <div className="cliente-page-section-header">
              <div>
                <p>Plataforma</p>
                <h2>Avisos operacionais</h2>
              </div>
              <span>{loading ? "Carregando..." : "Sino, Relatórios e e-mail"}</span>
            </div>

            <div className="template-email-notices">
              {avisos.map((aviso) => {
                const label = noticeLabels[aviso.tipo];

                return (
                  <article
                    className={`template-email-notice ${aviso.ativo ? "active" : ""
                      }`}
                    key={aviso.tipo}
                  >
                    <div className="template-email-notice-header">
                      <span className="template-email-notice-icon">{label.icon}</span>
                      <div>
                        <small>{label.scope}</small>
                        <strong>{label.title}</strong>
                      </div>
                      <label
                        className="template-email-switch"
                        title={aviso.ativo ? "Desativar aviso" : "Ativar aviso"}
                      >
                        <input
                          type="checkbox"
                          checked={aviso.ativo}
                          disabled={savingNotice === aviso.tipo}
                          onChange={() => toggleNotice(aviso)}
                        />
                        <span />
                      </label>
                    </div>

                    <label>
                      Título
                      <input
                        type="text"
                        maxLength={140}
                        value={aviso.titulo}
                        onChange={(event) =>
                          updateNoticeField(
                            aviso.tipo,
                            "titulo",
                            event.target.value
                          )
                        }
                      />
                    </label>
                    <label>
                      Mensagem
                      <textarea
                        maxLength={2000}
                        rows={4}
                        value={aviso.mensagem}
                        onChange={(event) =>
                          updateNoticeField(
                            aviso.tipo,
                            "mensagem",
                            event.target.value
                          )
                        }
                      />
                    </label>
                    <div className="template-email-notice-footer">
                      <span>
                        {aviso.ativo ? "Ativo" : "Inativo"} ·{" "}
                        {formatDateTime(aviso.atualizadoEm || aviso.criadoEm)}
                      </span>
                      <button
                        className="cliente-page-button secondary"
                        type="button"
                        disabled={savingNotice === aviso.tipo}
                        onClick={() => saveNotice(aviso)}
                      >
                        <FiSave />
                        {savingNotice === aviso.tipo
                          ? "Salvando..."
                          : "Salvar"}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="cliente-page-panel template-email-panel template-email-tax-panel">
            <div className="cliente-page-section-header">
              <div>
                <p>Fiscal</p>
                <h2>Informe de Imposto de Renda</h2>
              </div>
              <label
                className="template-email-switch"
                title={
                  taxReportNotice.ativo
                    ? "Bloquear geração"
                    : "Liberar geração"
                }
              >
                <input
                  type="checkbox"
                  checked={taxReportNotice.ativo}
                  disabled={savingTaxReport}
                  onChange={() =>
                    saveTaxReportAvailability(!taxReportNotice.ativo)
                  }
                />
                <span />
              </label>
            </div>

            <form
              className="template-email-report-form"
              onSubmit={(event) => {
                event.preventDefault();
                saveTaxReportAvailability();
              }}
            >
              <div className="template-email-form-grid">
                <label>
                  Título
                  <input
                    type="text"
                    maxLength={140}
                    required
                    value={taxReportNotice.titulo}
                    onChange={(event) =>
                      updateTaxReportNoticeField("titulo", event.target.value)
                    }
                  />
                </label>
                <label>
                  Ano-calendário
                  <input
                    type="number"
                    min={2000}
                    max={previousCalendarYear}
                    required
                    value={taxReportYear}
                    onChange={(event) =>
                      handleTaxReportYearChange(event.target.value)
                    }
                  />
                </label>
              </div>

              <label>
                Mensagem
                <textarea
                  maxLength={2000}
                  rows={4}
                  required
                  value={taxReportNotice.mensagem}
                  onChange={(event) =>
                    updateTaxReportNoticeField("mensagem", event.target.value)
                  }
                />
              </label>

              <div className="template-email-tax-footer">
                <span>
                  {taxReportNotice.ativo ? "Liberado" : "Bloqueado"} ·{" "}
                  {formatDateTime(
                    taxReportNotice.atualizadoEm || taxReportNotice.criadoEm
                  )}
                </span>
                <button
                  className="cliente-page-button"
                  type="submit"
                  disabled={savingTaxReport}
                >
                  <FiSave />
                  {savingTaxReport ? "Salvando..." : "Salvar liberação"}
                </button>
              </div>
            </form>

            <form
              className="template-email-tax-manual"
              onSubmit={gerarInformeManual}
            >
              <div className="template-email-tax-manual-header">
                <div>
                  <p>Geração manual</p>
                  <h3>Gerar para um cliente</h3>
                </div>
                <FiDownload />
              </div>

              <div className="template-email-form-grid">
                <label>
                  Cliente
                  <select
                    required
                    value={clienteInformeSelecionado}
                    disabled={loadingClientesInforme || gerandoInformeManual}
                    onChange={(event) =>
                      setClienteInformeSelecionado(event.target.value)
                    }
                  >
                    <option value="">
                      {loadingClientesInforme
                        ? "Carregando clientes..."
                        : "Selecione um cliente"}
                    </option>
                    {clientesInforme.map((cliente) => (
                      <option
                        key={cliente.idCredenciais}
                        value={cliente.idCredenciais}
                      >
                        {cliente.nome || cliente.email || cliente.idCredenciais}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Ano-calendário
                  <input
                    type="number"
                    min={2000}
                    max={previousCalendarYear}
                    required
                    value={anoInformeManual}
                    disabled={gerandoInformeManual}
                    onChange={(event) =>
                      setAnoInformeManual(Number(event.target.value))
                    }
                  />
                </label>
              </div>

              {clienteInformeAtual?.email && (
                <span className="template-email-tax-client-email">
                  {clienteInformeAtual.email}
                </span>
              )}

              <div className="template-email-tax-footer">
                <span>Download avulso para atendimento individual.</span>
                <button
                  className="cliente-page-button secondary"
                  type="submit"
                  disabled={
                    gerandoInformeManual ||
                    loadingClientesInforme ||
                    !clienteInformeSelecionado
                  }
                >
                  <FiDownload />
                  {gerandoInformeManual ? "Gerando..." : "Gerar PDF"}
                </button>
              </div>
            </form>
          </section>

          <section className="cliente-page-panel template-email-panel template-email-statement-panel">
            <div className="cliente-page-section-header">
              <div>
                <p>Extrato</p>
                <h2>Extrato de cliente</h2>
              </div>
              <FiDownload />
            </div>

            <form
              className="template-email-tax-manual template-email-statement-manual"
              onSubmit={gerarExtratoManual}
            >
              <div className="template-email-tax-manual-header">
                <div>
                  <p>Geração avulsa</p>
                  <h3>Gerar PDF por período</h3>
                </div>
                <FiFileText />
              </div>

              <div className="template-email-statement-grid">
                <label>
                  Cliente
                  <select
                    required
                    value={clienteExtratoSelecionado}
                    disabled={loadingClientesInforme || gerandoExtratoManual}
                    onChange={(event) =>
                      setClienteExtratoSelecionado(event.target.value)
                    }
                  >
                    <option value="">
                      {loadingClientesInforme
                        ? "Carregando clientes..."
                        : "Selecione um cliente"}
                    </option>
                    {clientesInforme.map((cliente) => (
                      <option
                        key={cliente.idCredenciais}
                        value={cliente.idCredenciais}
                      >
                        {cliente.nome || cliente.email || cliente.idCredenciais}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Serviço
                  <select
                    required
                    value={servicoExtrato}
                    disabled={gerandoExtratoManual}
                    onChange={(event) =>
                      setServicoExtrato(Number(event.target.value))
                    }
                  >
                    {statementServiceOptions.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Data inicial
                  <input
                    type="date"
                    required
                    value={dataInicialExtrato}
                    disabled={gerandoExtratoManual}
                    onChange={(event) =>
                      setDataInicialExtrato(event.target.value)
                    }
                  />
                </label>
                <label>
                  Data final
                  <input
                    type="date"
                    required
                    value={dataFinalExtrato}
                    disabled={gerandoExtratoManual}
                    onChange={(event) => setDataFinalExtrato(event.target.value)}
                  />
                </label>
              </div>

              {clienteExtratoAtual?.email && (
                <span className="template-email-tax-client-email">
                  {clienteExtratoAtual.email}
                </span>
              )}

              <div className="template-email-tax-footer">
                <span>Baixa o extrato consolidado do cliente selecionado.</span>
                <button
                  className="cliente-page-button"
                  type="submit"
                  disabled={
                    gerandoExtratoManual ||
                    loadingClientesInforme ||
                    !clienteExtratoSelecionado
                  }
                >
                  <FiDownload />
                  {gerandoExtratoManual ? "Gerando..." : "Gerar extrato PDF"}
                </button>
              </div>
            </form>
          </section>

          <section className="cliente-page-panel template-email-panel">
            <div className="cliente-page-section-header">
              <div>
                <p>Pesquisa</p>
                <h2>Nova Carta Mensal</h2>
              </div>
              <FiUploadCloud />
            </div>

            <form className="template-email-report-form" onSubmit={submitReport}>
              <div className="template-email-form-grid">
                <label>
                  Título
                  <input
                    type="text"
                    maxLength={140}
                    required
                    value={titulo}
                    onChange={(event) => setTitulo(event.target.value)}
                    placeholder="Carta Mensal Omex - cenário e posicionamento"
                  />
                </label>
                <label>
                  Mês de referência
                  <input
                    type="month"
                    required
                    value={mesReferencia}
                    onChange={(event) => setMesReferencia(event.target.value)}
                  />
                </label>
              </div>

              <label>
                Resumo
                <textarea
                  maxLength={2000}
                  rows={4}
                  required
                  value={descricao}
                  onChange={(event) => setDescricao(event.target.value)}
                  placeholder="Contexto da edição, principais temas e relação com as carteiras."
                />
              </label>

              <div className="template-email-upload-row">
                <label className="template-email-file-input">
                  <FiFileText />
                  <span>
                    {arquivo
                      ? `${arquivo.name} · ${formatBytes(arquivo.size)}`
                      : "Selecionar PDF"}
                  </span>
                  <input
                    key={fileInputKey}
                    type="file"
                    accept="application/pdf,.pdf"
                    required
                    onChange={handleFile}
                  />
                </label>
                <label className="template-email-publish-check">
                  <input
                    type="checkbox"
                    checked={publicar}
                    onChange={(event) => setPublicar(event.target.checked)}
                  />
                  Publicar e notificar agora
                </label>
                <button
                  className="cliente-page-button"
                  type="submit"
                  disabled={savingReport}
                >
                  <FiSend />
                  {savingReport ? "Enviando..." : "Publicar carta"}
                </button>
              </div>
            </form>
          </section>
        </div>

        <aside className="template-email-preview-panel" aria-label="Template aplicado">
          <div className="template-email-preview-card">
            <div className="template-email-preview-brand">
              <span>OMEX</span>
              <strong>Capital</strong>
            </div>
            <small>OmexLab delivery</small>
            <h3>Template transacional</h3>
            <p>
              Identidade visual Omex, chamada segura, conteúdo dinâmico e CTA
              para a área autenticada do cliente.
            </p>
            <div className="template-email-preview-tags">
              <span>Logo oficial</span>
              <span>Blockchain visual</span>
              <span>PDF fiscal</span>
            </div>
          </div>

          <div className="template-email-checklist">
            <article>
              <FiCheckCircle />
              <div>
                <strong>Cota e Sistema</strong>
                <span>Disparo apenas quando o aviso ativo muda.</span>
              </div>
            </article>
            <article>
              <FiCheckCircle />
              <div>
                <strong>Carta Mensal</strong>
                <span>PDF fica no acervo e segue anexado por e-mail.</span>
              </div>
            </article>
            <article>
              <FiCheckCircle />
              <div>
                <strong>Informe IR</strong>
                <span>Liberação anual controla Extrato, sino e e-mail.</span>
              </div>
            </article>
          </div>
        </aside>
      </section>

      <section className="cliente-page-panel template-email-panel">
        <div className="cliente-page-section-header">
          <div>
            <p>Acervo</p>
            <h2>Cartas mensais</h2>
          </div>
          <span>{cartas.length} documentos</span>
        </div>

        <div className="template-email-reports">
          {cartas.map((carta) => {
            const editing = editingCartaId === carta.id;
            const saving = savingCartaId === carta.id;

            return (
              <article className={editing ? "editing" : ""} key={carta.id}>
                <span className="template-email-report-icon">
                  <FiFileText />
                </span>
                <div>
                  <strong>{carta.titulo}</strong>
                  <small>
                    {formatReferenceMonth(carta.mesReferencia)} ·{" "}
                    {carta.nomeArquivo || "Carta Mensal"}
                  </small>
                </div>
                <span className={carta.ativo ? "published" : ""}>
                  {carta.ativo ? "Publicado" : "Oculto"}
                </span>
                {carta.urlArquivo && (
                  <a
                    href={carta.urlArquivo}
                    target="_blank"
                    rel="noreferrer"
                    title="Abrir PDF"
                  >
                    <FiEye />
                  </a>
                )}
                <button
                  type="button"
                  title="Editar dados"
                  disabled={saving}
                  onClick={() =>
                    editing ? cancelEditingCarta() : startEditingCarta(carta)
                  }
                >
                  {editing ? <FiX /> : <FiEdit2 />}
                </button>
                <label
                  className="template-email-report-action"
                  title="Substituir PDF"
                >
                  <FiUpload />
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    disabled={saving}
                    onChange={(event) => replaceCartaFile(carta, event)}
                  />
                </label>
                <button
                  type="button"
                  className={carta.ativo ? "active" : ""}
                  title={carta.ativo ? "Ocultar dos clientes" : "Publicar"}
                  disabled={saving}
                  onClick={() => toggleReport(carta)}
                >
                  {carta.ativo ? <FiEye /> : <FiEyeOff />}
                </button>
                <button
                  type="button"
                  className="danger"
                  title="Remover carta mensal"
                  disabled={saving}
                  onClick={() => deleteCarta(carta)}
                >
                  <FiTrash2 />
                </button>

                {editing && (
                  <form
                    className="template-email-report-edit"
                    onSubmit={saveCartaDetails}
                  >
                    <div className="template-email-form-grid">
                      <label>
                        Título
                        <input
                          type="text"
                          maxLength={140}
                          required
                          value={editDraft.titulo}
                          onChange={(event) =>
                            updateEditDraft("titulo", event.target.value)
                          }
                        />
                      </label>
                      <label>
                        Mês de referência
                        <input
                          type="month"
                          required
                          value={editDraft.mesReferencia}
                          onChange={(event) =>
                            updateEditDraft("mesReferencia", event.target.value)
                          }
                        />
                      </label>
                    </div>
                    <label>
                      Resumo
                      <textarea
                        maxLength={2000}
                        rows={3}
                        required
                        value={editDraft.descricao}
                        onChange={(event) =>
                          updateEditDraft("descricao", event.target.value)
                        }
                      />
                    </label>
                    <div className="template-email-report-edit-actions">
                      <button
                        className="cliente-page-button secondary"
                        type="button"
                        disabled={saving}
                        onClick={cancelEditingCarta}
                      >
                        <FiX />
                        Cancelar
                      </button>
                      <button
                        className="cliente-page-button"
                        type="submit"
                        disabled={saving}
                      >
                        <FiSave />
                        {saving ? "Salvando..." : "Salvar dados"}
                      </button>
                    </div>
                  </form>
                )}
              </article>
            );
          })}
          {!cartas.length && !loading && (
            <div className="cliente-page-empty">
              Nenhuma Carta Mensal enviada.
            </div>
          )}
        </div>
      </section>
    </ClientePageShell>
  );
};

export default PainelControle;
